import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ParticleBackground from '@/components/3d/ParticleBackground';
import { useLanguage } from '@/hooks/useLanguage';
import { useContent } from '@/hooks/useContent';
import { ChevronDown } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const { t } = useContent();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    const title = titleRef.current;

    if (!hero || !title) return;

    // Title animation
    gsap.fromTo(
      title,
      {
        opacity: 0,
        y: 0,
        scale: 0.9,
      },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.5,
        ease: 'power3.out',
      }
    );

    // Scroll parallax
    ScrollTrigger.create({
      trigger: hero,
      start: 'top top',
      end: 'bottom top',
      scrub: 1,
      onUpdate: (self) => {
        const progress = self.progress;
        gsap.to(title, {
          y: progress * 60,
          opacity: 1 - progress * 0.6,
          scale: 1 - progress * 0.1,
        });
      },
    });

  }, [t]);

  const scrollToNext = () => {
    const nextSection = document.querySelector('.home-page > *:nth-child(2)');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'auto' });
    }
  };

  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black dark:from-dark-primary dark:via-dark-secondary dark:to-dark-primary"
    >
      <ParticleBackground mousePosition={{ x: 0, y: 0 }} />

      <div className="container mx-auto px-4 relative z-10 text-center">
        <motion.h1
          ref={titleRef}
          className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 text-center"
          style={{
            fontWeight: 700,
            letterSpacing: language === 'ar' ? '0' : '-0.03em',
            lineHeight: 'calc(1em + 20px)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          {t('home.hero.title')}
        </motion.h1>

        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          {t('home.hero.description')}
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          <motion.button
            onClick={() => navigate('/home-visit-request')}
            className="px-8 py-4 bg-gradient-to-r from-brand-orange-base to-brand-orange-light text-white rounded-lg font-medium hover:bg-brand-orange-light transition-colors shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('home.hero.cta')}
          </motion.button>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <button
          onClick={scrollToNext}
          className="text-gray-400 hover:text-white transition-colors"
          aria-label="Scroll to next section"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </motion.div>
    </section>
  );
};

export default Hero;
